import { useState, useEffect, useCallback } from "react";
import { GitCompare, AlertTriangle, AlertCircle, CheckCircle, RefreshCw, Building2 } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";
import {
  CepEnergyPanel, ReconciliationPanel, ExcludedDepartmentsPanel, BlockedNote
} from "@/components/views/CepEnergyPanels";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const ComparisonView = ({ clientId, assetUuid }) => {
  const t = useT();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Asset-scoped: the selected building narrows the departments, the inventory
  // and IoT rows, and the CEP energy the reconciliation compares against. With
  // no selection the read is client-wide, which is a real request rather than
  // an unresolved one.
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clients/${clientId}/comparison/dashboard`, {
        params: assetUuid ? { asset_uuid: assetUuid } : {},
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching comparison dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId, assetUuid]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const deptData = dashboard?.departments || [];
  const discrepancies = dashboard?.discrepancies || [];
  const summ = dashboard?.summary || {};
  const energy = dashboard?.energy;
  const reconciliation = dashboard?.reconciliation;
  const excluded = dashboard?.excluded_departments || [];
  const isAssetScoped = dashboard?.department_scope === "asset";

  return (
    <div data-testid="comparison-dashboard">
      <div className="page-header flex-between">
        <div>
          <h2 className="page-title">{t("comparison_title")}</h2>
          <p className="page-subtitle">{t("comparison_subtitle")}</p>
          <div className="scope-chip" data-testid="comparison-scope">
            <Building2 size={13} />
            <span>{isAssetScoped ? t("scope_building") : t("scope_client")}</span>
            <span className="text-muted">
              {isAssetScoped ? t("scope_asset_note") : t("scope_client_note")}
            </span>
          </div>
        </div>
        <button className="btn btn-primary" onClick={fetchDashboard}>
          <RefreshCw size={16} /> {t("refresh")}
        </button>
      </div>

      {discrepancies.length > 0 && (
        <div className="discrepancy-alerts mb-lg" data-testid="discrepancy-alerts">
          <div className="discrepancy-alerts-header">
            <AlertTriangle size={20} />
            <span>{discrepancies.length} Discrepanc{discrepancies.length > 1 ? "ies" : "y"} Detected</span>
            <span className="badge badge-na">{summ.high_severity || 0} High</span>
            <span className="badge badge-neutral">{summ.medium_severity || 0} Medium</span>
          </div>
          {discrepancies.map((d, i) => (
            <div key={i} className={`discrepancy-alert-item ${d.severity}`} data-testid={`discrepancy-${i}`}>
              <div className="discrepancy-dept">{d.department}</div>
              <div className="discrepancy-metric">{d.metric}</div>
              <div className="discrepancy-values">
                <span>Inventory: <strong>{d.inventory_value?.toLocaleString()}</strong></span>
                <span>IoT: <strong>{d.iot_value?.toLocaleString()}</strong></span>
              </div>
              <div className={`discrepancy-variance ${d.variance_pct > 0 ? "over" : "under"}`}>
                {d.variance_pct > 0 ? "+" : ""}{d.variance_pct}%
              </div>
            </div>
          ))}
        </div>
      )}

      {discrepancies.length === 0 && (
        <div className="card mb-lg" style={{ borderLeft: "4px solid var(--success)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
            <CheckCircle size={20} style={{ color: "var(--success)" }} />
            <span>No significant discrepancies detected between data sources. Upload inventory data and simulate IoT readings to enable comparison.</span>
          </div>
        </div>
      )}

      <div className="stats-grid mb-lg">
        <div className="stat-card">
          <div className="stat-icon primary"><GitCompare size={24} /></div>
          <div className="stat-value">{summ.total_departments}</div>
          <div className="stat-label">{t("departments_compared")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning"><AlertTriangle size={24} /></div>
          <div className="stat-value">{summ.total_discrepancies}</div>
          <div className="stat-label">{t("discrepancies")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon danger"><AlertCircle size={24} /></div>
          <div className="stat-value">{summ.high_severity}</div>
          <div className="stat-label">{t("high_severity")}</div>
        </div>
      </div>

      {/* The energy data as a comparison source, and the upward reconciliation
          against it. Both read db.ceps. */}
      <CepEnergyPanel energy={energy} />
      <ReconciliationPanel reconciliation={reconciliation} />
      <ExcludedDepartmentsPanel excluded={excluded} />

      <div className="card mb-lg">
        <h4 className="card-title">{t("inv_vs_iot_vs_audit")}</h4>
        {/* No CEP column exists in this table on purpose: CEP data has no
            department dimension and no allocation share splits it. */}
        {deptData.length > 0 && deptData[0].energy?.blocked && (
          <BlockedNote reason={deptData[0].energy.blocked} />
        )}
        <div className="table-container">
          <table className="table" data-testid="comparison-table">
            <thead>
              <tr>
                <th>Department</th>
                <th>Audit %</th>
                <th>Inv. Energy (kWh)</th>
                <th>Inv. Power (kW)</th>
                <th>IoT Energy (kWh)</th>
                <th>IoT Power (kW)</th>
                <th>IoT PF</th>
                <th>IoT V (avg)</th>
                <th>Energy Var. %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {deptData.map((dept, idx) => (
                <tr key={idx} className={dept.variance?.has_discrepancy ? "row-discrepancy" : ""}>
                  <td><strong>{dept.department}</strong></td>
                  <td>{dept.audit?.completion}%</td>
                  <td>{dept.inventory?.energy_kwh?.toLocaleString()}</td>
                  <td>{dept.inventory?.power_kw?.toLocaleString()}</td>
                  <td>{dept.iot?.energy_total_kwh?.toLocaleString()}</td>
                  <td>{dept.iot?.active_power_kw?.toLocaleString()}</td>
                  <td>{dept.iot?.power_factor || "-"}</td>
                  <td>{dept.iot?.voltage_avg_v || "-"}</td>
                  <td className={`${Math.abs(dept.variance?.energy_pct) > 10 ? "text-danger" : ""}`}>
                    {dept.variance?.energy_pct !== 0 ? `${dept.variance?.energy_pct > 0 ? "+" : ""}${dept.variance?.energy_pct}%` : "-"}
                  </td>
                  <td>
                    {dept.variance?.has_discrepancy ? (
                      <span className="badge badge-na"><AlertTriangle size={12} /> Alert</span>
                    ) : (
                      <span className="badge badge-success"><CheckCircle size={12} /> OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bento-grid mb-lg">
        <div className="card">
          <h4 className="card-title">Energy Comparison: Inventory vs IoT (kWh)</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={deptData.map(d => ({
              name: d.department.split(" ")[0],
              inventory: d.inventory?.energy_kwh || 0,
              iot: d.iot?.energy_total_kwh || 0,
              audit: d.audit?.energy_kwh || 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
              <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 11 }} />
              <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="inventory" fill="#244C36" name="Inventory" radius={[4, 4, 0, 0]} />
              <Bar dataKey="iot" fill="#3B82F6" name="IoT (PMC-53A-E)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="audit" fill="#F59E0B" name="Audit" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h4 className="card-title">Active Power: Inventory vs IoT (kW)</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={deptData.map(d => ({
              name: d.department.split(" ")[0],
              inventory: d.inventory?.power_kw || 0,
              iot: d.iot?.active_power_kw || 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
              <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 11 }} />
              <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="inventory" fill="#244C36" name="Inventory (kW)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="iot" fill="#3B82F6" name="IoT (kW)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <h4 className="card-title">IoT PMC-53A-E Meter Readings by Department</h4>
        <div className="pmc-grid">
          {deptData.map((dept) => (
            <div className="pmc-card" key={dept.department_key} data-testid={`pmc-card-${dept.department_key}`}>
              <h5 className="pmc-card-title">{dept.department}</h5>
              <div className="pmc-readings">
                <div className="pmc-reading">
                  <span className="pmc-label">Active Power</span>
                  <span className="pmc-value">{dept.iot?.active_power_kw} <small>kW</small></span>
                </div>
                <div className="pmc-reading">
                  <span className="pmc-label">Energy Total</span>
                  <span className="pmc-value">{dept.iot?.energy_total_kwh?.toLocaleString()} <small>kWh</small></span>
                </div>
                <div className="pmc-reading">
                  <span className="pmc-label">Power Factor</span>
                  <span className="pmc-value">{dept.iot?.power_factor || "-"}</span>
                </div>
                <div className="pmc-reading">
                  <span className="pmc-label">Voltage (avg)</span>
                  <span className="pmc-value">{dept.iot?.voltage_avg_v || "-"} <small>V</small></span>
                </div>
                <div className="pmc-reading">
                  <span className="pmc-label">Current (avg)</span>
                  <span className="pmc-value">{dept.iot?.current_avg_a || "-"} <small>A</small></span>
                </div>
              </div>
              {dept.iot?.reading_count === 0 && (
                <div className="text-muted" style={{ fontSize: "var(--text-small)", textAlign: "center", marginTop: "var(--space-sm)" }}>
                  No IoT readings — run simulation first
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;
